/**
 * DatePicker — pole daty z WŁASNYM stylowanym kalendarzem (task #14, Faza 1).
 *
 * Powód powstania: natywny `<input type="date">` otwiera kalendarz SYSTEMOWY (OS),
 * którego nie da się ostylować pod motyw/dark mode/white-label apki. Tutaj pole jest
 * przyciskiem stylizowanym jak `Input` (pokazuje datę w formacie `dd.mm.rrrr`), a klik
 * otwiera popover (Radix `@radix-ui/react-popover`) z własną siatką miesiąca — panel,
 * hover/selected w kolorze AKCENTU, tokeny semantyczne. Zamykanie klikiem poza / Esc
 * (Radix), pełna obsługa klawiatury i ARIA.
 *
 * Kalendarz jest minimalny i własny (bez `react-day-picker`) — pełna kontrola tokenów,
 * mniejszy bundle, deterministyczne testy. Nazwy miesięcy/dni z `Intl` (locale `pl`),
 * więc bez nowych kluczy i18n poza nawigacją/„wyczyść".
 *
 * Wartość WEWNĘTRZNA i emitowana: `YYYY-MM-DD` (albo `''` = brak). API zbliżone do
 * `<input type="date">`, żeby migracja (Faza 2) była mechaniczna.
 *
 * Tryb `mode="month"` (domyślnie `'day'`): popover pokazuje rok ze strzałkami i siatkę
 * 4×3 miesięcy; wybór emituje pierwszy dzień miesiąca (`YYYY-MM-01`), a pole pokazuje
 * etykietę „wrzesień 2026" (język z `i18n.language`). Służy do pól „miesiąc księgowy"
 * (np. Data okresu). Opcjonalne `minYear`/`maxYear` ograniczają nawigację rokiem
 * (strzałki wyłączone na granicy, miesiące spoza zakresu nieklikalne).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';

import { IconButton } from '@/components/IconButton';
import { formatMonthLabel } from '@/lib/month-label';
import { cn } from '@/lib/utils';

/**
 * Publiczny znacznik panelu kalendarza (treści popovera w portalu, poza polem). Pozwala
 * wołającemu rozpoznać „klik wewnątrz panelu" (`target.closest(DATE_PICKER_PANEL_SELECTOR)`), np.
 * komórce edytowanej w wierszu, która zamyka się po kliknięciu poza sobą, ale nie po kliknięciu w
 * nawigację miesiąca. Atrybut jest stabilnym kontraktem — nie polegaj na wewnętrznych klasach ani
 * na wrapperze Radixa (`data-radix-popper-content-wrapper`).
 */
export const DATE_PICKER_PANEL_SELECTOR = '[data-datepicker-panel]';

export interface DatePickerProps {
  /** Wartość w formacie `YYYY-MM-DD` lub `''` (brak). W trybie `month` emitowane `YYYY-MM-01`. */
  value: string;
  /** `day` (domyślnie) — wybór dnia; `month` — wybór miesiąca (emisja `YYYY-MM-01`). */
  mode?: 'day' | 'month';
  /** Tylko `mode="month"`: najwcześniejszy dozwolony rok (włącznie). Brak = bez dolnej granicy. */
  minYear?: number;
  /** Tylko `mode="month"`: najpóźniejszy dozwolony rok (włącznie). Brak = bez górnej granicy. */
  maxYear?: number;
  onChange: (value: string) => void;
  disabled?: boolean;
  /**
   * Panel kalendarza jest otwarty od pierwszego renderu — dla pola, które pojawia się dopiero po
   * kliknięciu „edytuj" (wybór daty ma wtedy kosztować jeden klik, nie dwa). Liczy się tylko przy
   * montażu: późniejsza zmiana propsa jest ignorowana, a dalej panel otwiera i zamyka użytkownik.
   */
  defaultOpen?: boolean;
  /** Podświetla ramkę pola na czerwono (sygnał błędu; tekst błędu renderuje wołający). */
  invalid?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'data-testid'?: string;
}

// ── helpery dat (bez zależności; strefa lokalna, część dzienna) ──────────────

/**
 * Lokalna data z roku/miesiąca/dnia. `new Date(rok, …)` traktuje lata 0–99 jako 1900–1999,
 * więc rok ustawiamy przez `setFullYear` — `0026-09-01` nie zamienia się cicho w 1926.
 * Dzień spoza miesiąca przelewa się jak w `new Date` (np. dzień 0 = ostatni poprzedniego).
 */
function localDate(year: number, month: number, day: number): Date {
  const date = new Date(2000, 0, 1);
  date.setFullYear(year, month, day);
  return date;
}

/** `YYYY-MM-DD` → Date (lokalny północ) lub null, gdy pusto/niepoprawnie. */
function parseIso(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const [, y, mo, d] = m;
  const date = localDate(Number(y), Number(mo) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Date → `YYYY-MM-DD` (część dzienna, lokalnie; rok zawsze 4-cyfrowy). */
function toIso(date: Date): string {
  const y = String(date.getFullYear()).padStart(4, '0');
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${mo}-${d}`;
}

/** `YYYY-MM-DD` → `dd.mm.rrrr` (wyświetlanie). */
function toDisplay(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return '';
  const [, y, mo, d] = m;
  return `${d}.${mo}.${y}`;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Siatka 6×7 dni obejmująca podany miesiąc (tydzień od poniedziałku). */
function buildMonthGrid(viewYear: number, viewMonth: number): Date[] {
  const first = localDate(viewYear, viewMonth, 1);
  // getDay(): 0=niedziela … przesuwamy tak, by tydzień zaczynał się w poniedziałek.
  const offset = (first.getDay() + 6) % 7;
  const start = localDate(viewYear, viewMonth, 1 - offset);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

export function DatePicker({
  value,
  mode = 'day',
  minYear,
  maxYear,
  onChange,
  disabled,
  defaultOpen = false,
  invalid,
  placeholder,
  className,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'data-testid': dataTestId,
}: DatePickerProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'pl';
  const [open, setOpen] = useState(defaultOpen);

  const selected = useMemo(() => parseIso(value), [value]);
  const today = useMemo(() => new Date(), []);

  // Miesiąc widoczny w kalendarzu — startuje od wybranej daty (albo dziś).
  const [view, setView] = useState(() => {
    const base = selected ?? today;
    return { year: base.getFullYear(), month: base.getMonth() };
  });

  // Gdy popover się otwiera, przeskocz na miesiąc aktualnej wartości.
  const handleOpenChange = (next: boolean) => {
    if (next) {
      const base = parseIso(value) ?? today;
      setView({ year: base.getFullYear(), month: base.getMonth() });
    }
    setOpen(next);
  };

  const grid = useMemo(() => buildMonthGrid(view.year, view.month), [view]);

  const monthLabel = useMemo(
    () =>
      new Intl.DateTimeFormat('pl', { month: 'long', year: 'numeric' }).format(
        localDate(view.year, view.month, 1),
      ),
    [view],
  );

  const weekdayLabels = useMemo(() => {
    const fmt = new Intl.DateTimeFormat('pl', { weekday: 'short' });
    // 2024-01-01 to poniedziałek — 7 kolejnych dni = pn…nd.
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 0, 1 + i)));
  }, []);

  const goPrevMonth = () =>
    setView((v) =>
      v.month === 0 ? { year: v.year - 1, month: 11 } : { ...v, month: v.month - 1 },
    );
  const goNextMonth = () =>
    setView((v) =>
      v.month === 11 ? { year: v.year + 1, month: 0 } : { ...v, month: v.month + 1 },
    );

  const pickDay = (day: Date) => {
    onChange(toIso(day));
    setOpen(false);
  };

  const isMonthMode = mode === 'month';

  // Tryb miesiąca: skrócone nazwy 12 miesięcy (Intl, język z i18n).
  const shortMonthLabels = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, { month: 'short' });
    return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(2024, i, 1)));
  }, [locale]);

  const prevYearDisabled = minYear !== undefined && view.year <= minYear;
  const nextYearDisabled = maxYear !== undefined && view.year >= maxYear;
  const yearOutOfRange =
    (minYear !== undefined && view.year < minYear) ||
    (maxYear !== undefined && view.year > maxYear);

  const goPrevYear = () => setView((v) => ({ ...v, year: v.year - 1 }));
  const goNextYear = () => setView((v) => ({ ...v, year: v.year + 1 }));

  /** `YYYY-MM-01` dla miesiąca (0–11) widocznego roku — bez `new Date` (lata < 100). */
  const monthIso = (month: number) =>
    `${String(view.year).padStart(4, '0')}-${String(month + 1).padStart(2, '0')}-01`;

  const pickMonth = (month: number) => {
    onChange(monthIso(month));
    setOpen(false);
  };

  const display = isMonthMode
    ? (formatMonthLabel(parseIso(value) ? value : null, locale) ?? '')
    : toDisplay(value);
  const emptyLabel = isMonthMode ? t('ui.datePicker.monthPlaceholder') : 'dd.mm.rrrr';

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <div className={cn('relative', className)}>
        <PopoverPrimitive.Trigger asChild>
          <button
            type="button"
            id={id}
            disabled={disabled}
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedBy}
            aria-invalid={invalid ? true : undefined}
            data-testid={dataTestId}
            className={cn(
              'flex w-full items-center gap-2 rounded-lg border bg-bg-primary px-3 py-2 text-left text-sm text-text-primary transition-colors',
              'focus:outline-none focus:ring-2 data-[state=open]:ring-2',
              // Stan błędu zostaje czerwony także po fokusie i przy otwartym kalendarzu —
              // wtedy użytkownik poprawia pole (ten sam wzorzec co `Input`).
              invalid
                ? 'border-[rgb(var(--color-error))] focus:border-[rgb(var(--color-error))] focus:ring-[rgb(var(--color-error))] data-[state=open]:border-[rgb(var(--color-error))] data-[state=open]:ring-[rgb(var(--color-error))]'
                : 'border-border-primary focus:ring-accent-500 focus:border-transparent data-[state=open]:ring-accent-500 data-[state=open]:border-transparent',
              'disabled:cursor-not-allowed disabled:bg-bg-tertiary disabled:text-text-muted',
              value ? 'pr-9' : '',
            )}
          >
            <Calendar className="h-4 w-4 flex-shrink-0 text-text-muted" aria-hidden="true" />
            <span className={cn('truncate', !display && 'text-text-muted')}>
              {display || placeholder || emptyLabel}
            </span>
          </button>
        </PopoverPrimitive.Trigger>

        {value && !disabled && (
          <IconButton
            size="sm"
            aria-label={t('ui.datePicker.clear')}
            onClick={() => onChange('')}
            className="absolute right-1 top-1/2 -translate-y-1/2"
            data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
          >
            <X />
          </IconButton>
        )}
      </div>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          data-datepicker-panel=""
          className={cn(
            'z-[10001] w-[17rem] rounded-lg border border-border-primary bg-bg-elevated p-2 shadow-lg',
            'focus:outline-none',
          )}
        >
          {isMonthMode && (
            <>
              {/* Nagłówek: nawigacja rokiem */}
              <div className="mb-1 flex items-center justify-between">
                <IconButton
                  size="sm"
                  aria-label={t('ui.datePicker.prevYear')}
                  onClick={goPrevYear}
                  disabled={prevYearDisabled}
                  data-testid={dataTestId ? `${dataTestId}-prev-year` : undefined}
                >
                  <ChevronLeft />
                </IconButton>
                <span
                  className="text-sm font-medium text-text-primary"
                  aria-live="polite"
                  data-testid={dataTestId ? `${dataTestId}-year` : undefined}
                >
                  {view.year}
                </span>
                <IconButton
                  size="sm"
                  aria-label={t('ui.datePicker.nextYear')}
                  onClick={goNextYear}
                  disabled={nextYearDisabled}
                  data-testid={dataTestId ? `${dataTestId}-next-year` : undefined}
                >
                  <ChevronRight />
                </IconButton>
              </div>

              {/* Siatka miesięcy: 4 wiersze po 3 kolumny (`role="row"` — poprawna struktura grid). */}
              <div className="space-y-1" role="grid" aria-label={String(view.year)}>
                {[0, 1, 2, 3].map((rowIndex) => (
                  <div key={rowIndex} className="grid grid-cols-3 gap-1" role="row">
                    {shortMonthLabels.slice(rowIndex * 3, rowIndex * 3 + 3).map((label, col) => {
                      const month = rowIndex * 3 + col;
                      const isSelected =
                        selected !== null &&
                        selected.getFullYear() === view.year &&
                        selected.getMonth() === month;
                      return (
                        <button
                          key={month}
                          type="button"
                          role="gridcell"
                          aria-selected={isSelected}
                          aria-label={formatMonthLabel(monthIso(month), locale) ?? label}
                          disabled={yearOutOfRange}
                          onClick={() => pickMonth(month)}
                          data-testid={
                            dataTestId
                              ? `${dataTestId}-month-${view.year}-${String(month + 1).padStart(2, '0')}`
                              : undefined
                          }
                          className={cn(
                            'flex h-9 items-center justify-center rounded-md text-sm capitalize outline-none transition-colors',
                            'focus-visible:ring-2 focus-visible:ring-accent-500',
                            'text-text-primary',
                            'disabled:cursor-not-allowed disabled:opacity-40',
                            !isSelected && !yearOutOfRange && 'hover:bg-accent-500/15',
                            isSelected &&
                              'bg-accent-600 font-medium text-white hover:bg-accent-600',
                          )}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </>
          )}

          {!isMonthMode && (
            <>
              {/* Nagłówek: nawigacja miesiącem */}
              <div className="mb-1 flex items-center justify-between">
                <IconButton
                  size="sm"
                  aria-label={t('ui.datePicker.prevMonth')}
                  onClick={goPrevMonth}
                >
                  <ChevronLeft />
                </IconButton>
                <span className="text-sm font-medium capitalize text-text-primary">
                  {monthLabel}
                </span>
                <IconButton
                  size="sm"
                  aria-label={t('ui.datePicker.nextMonth')}
                  onClick={goNextMonth}
                >
                  <ChevronRight />
                </IconButton>
              </div>

              {/* Nagłówki dni tygodnia */}
              <div className="grid grid-cols-7 gap-0.5">
                {weekdayLabels.map((wd, i) => (
                  <span
                    key={i}
                    className="flex h-7 items-center justify-center text-[11px] font-medium capitalize text-text-muted"
                    aria-hidden="true"
                  >
                    {wd}
                  </span>
                ))}
              </div>

              {/* Siatka dni */}
              <div className="grid grid-cols-7 gap-0.5" role="grid">
                {grid.map((day) => {
                  const inMonth = day.getMonth() === view.month;
                  const isSelected = selected ? sameDay(day, selected) : false;
                  const isToday = sameDay(day, today);
                  return (
                    <button
                      key={day.getTime()}
                      type="button"
                      role="gridcell"
                      aria-selected={isSelected}
                      aria-current={isToday ? 'date' : undefined}
                      onClick={() => pickDay(day)}
                      data-testid={
                        dataTestId && inMonth ? `${dataTestId}-day-${toIso(day)}` : undefined
                      }
                      className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-md text-sm outline-none transition-colors',
                        'focus-visible:ring-2 focus-visible:ring-accent-500',
                        inMonth ? 'text-text-primary' : 'text-text-muted/60',
                        !isSelected && 'hover:bg-accent-500/15',
                        isSelected && 'bg-accent-600 font-medium text-white hover:bg-accent-600',
                        isToday && !isSelected && 'ring-1 ring-inset ring-accent-500',
                      )}
                    >
                      {day.getDate()}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
